import { useEffect, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KeyRound, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeToggle } from '@/components/ThemeToggle';
import { TarabeezaMark } from '@/components/TarabeezaMark';
import { useAppDispatch, useAppSelector } from '@/Redux/store';
import {
  acknowledgeDiscardedCart,
  clearPinPrompt,
  openQrSession,
} from '@/Redux/Slices/guestSlice';

/**
 * Entry point. The QR carries `?t=<token>`; the PIN box appears only once the
 * API has said this table needs one, so the common case stays a single scan with
 * nothing to type.
 */
export default function Scan() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [params] = useSearchParams();
  const { session, status, error, pinRequired, discardedCart } = useAppSelector((s) => s.guest);
  const [pin, setPin] = useState('');
  const [typedToken, setTypedToken] = useState('');

  const qrToken = params.get('t') ?? '';
  const token = qrToken || typedToken.trim();

  /**
   * A `?t=` in the URL is an instruction to open **that** table.
   *
   * The stored session only satisfies it if it came from this same token. A
   * session restored from `localStorage` is `status: 'ready'`, so gating the
   * open on `status === 'idle'` meant a guest who scanned a different table
   * kept the old session and every order they placed landed on the previous
   * table's check. Compare the tokens instead.
   */
  const isCurrentTable = !!session && !!qrToken && session.qrToken === qrToken;

  // One automatic attempt per token. A refusal (a bad token, or a table whose
  // PIN the venue is waiting for) must not re-dispatch in a loop, and the PIN
  // retry is an explicit tap; a *different* token is always tried.
  const attempted = useRef<string | null>(null);

  useEffect(() => {
    if (!qrToken || isCurrentTable) return;
    if (attempted.current === qrToken) return;
    attempted.current = qrToken;
    void dispatch(openQrSession({ qrToken }));
  }, [qrToken, isCurrentTable, dispatch]);

  // The notice belongs to this screen; leaving it is acknowledging it.
  useEffect(
    () => () => {
      dispatch(acknowledgeDiscardedCart());
    },
    [dispatch],
  );

  // Only leave for the menu once the session in hand is this table's.
  if (session && (!qrToken || isCurrentTable)) return <Navigate to="/menu" replace />;

  const open = () => {
    if (token) void dispatch(openQrSession({ qrToken: token, pin: pinRequired ? pin : undefined }));
  };

  // Changing tables clears the basket (see `openQrSession.pending`); the guest
  // is told so here rather than finding it empty on the menu.
  const cartNotice = discardedCart > 0 && (
    <p className="text-sm text-notice" role="status">
      {t('guest.scan.cartCleared', { count: discardedCart })}
    </p>
  );

  return (
    <div className="grid min-h-dvh place-items-center bg-background p-6">
      <div className="w-full max-w-sm space-y-4">
        {/* The two settings a guest may need before they can read anything on
            this screen. Both are here rather than behind the tab bar, because
            the tab bar does not exist until a table is open. */}
        <div className="flex items-center justify-between gap-2">
          <ThemeToggle />
          <LanguageSwitcher />
        </div>

        {/* The brand moment: the first — and for a guest who never installs
            anything, possibly the only — place the mark is seen at a size where
            it reads as a table rather than as a favicon. */}
        <div className="flex flex-col items-center gap-3 pb-1 pt-2">
          <TarabeezaMark size={88} decorative />
          <div className="space-y-0.5 text-center">
            <div className="text-2xl font-semibold tracking-tight">{t('guest.brandName')}</div>
            <p className="text-sm text-muted-foreground">{t('guest.scan.tagline')}</p>
          </div>
        </div>

        <Card>
          <CardContent className="space-y-4 p-6 text-center">
            {pinRequired ? (
              <>
                <KeyRound className="mx-auto h-12 w-12 text-accent" />
                <div className="space-y-1">
                  <h1 className="text-lg font-semibold">{t('guest.scan.pinTitle')}</h1>
                  <p className="text-sm text-muted-foreground">{t('guest.scan.pinHint')}</p>
                </div>

                <div className="space-y-2 text-start">
                  <label className="text-sm font-medium" htmlFor="table-pin">
                    {t('guest.scan.pinLabel')}
                  </label>
                  <Input
                    id="table-pin"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    placeholder="••••"
                    className="text-center text-lg tracking-[0.5em]"
                  />
                </div>

                {cartNotice}
                {status === 'error' && error && <p className="text-sm text-negative">{error}</p>}

                <Button className="w-full" size="lg" disabled={!pin} onClick={open}>
                  {status === 'loading' ? t('common.loading') : t('common.confirm')}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setPin('');
                    dispatch(clearPinPrompt());
                  }}
                >
                  {t('common.back')}
                </Button>
              </>
            ) : (
              <>
                <QrCode className="mx-auto h-12 w-12 text-accent" />
                <div className="space-y-1">
                  <h1 className="text-lg font-semibold">{t('guest.scan.title')}</h1>
                  <p className="text-sm text-muted-foreground">{t('guest.scan.hint')}</p>
                </div>

                {cartNotice}
                {status === 'loading' && qrToken ? (
                  <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
                ) : (
                  <>
                    {status === 'error' && error && <p className="text-sm text-negative">{error}</p>}
                    <div className="space-y-2 text-start">
                      <label className="text-sm font-medium" htmlFor="table-code">
                        {t('guest.scan.codeLabel')}
                      </label>
                      <Input
                        id="table-code"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        value={qrToken || typedToken}
                        readOnly={!!qrToken}
                        onChange={(e) => setTypedToken(e.target.value)}
                        placeholder={t('guest.scan.codePlaceholder')}
                      />
                    </div>
                    <Button className="w-full" size="lg" disabled={!token} onClick={open}>
                      {t('guest.scan.open')}
                    </Button>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
